import React, { useEffect, useMemo, useState, useRef, useContext } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Client } from 'colyseus.js';
import api, { getAuthIdToken } from '../api';
import emailIcon from '../img/email.png';
import whatsappIcon from '../img/whatsapp.png';
import messengerIcon from '../img/messenger.png';
import linkedinIcon from '../img/linkedin.png';
import { X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { ThemeContext } from '../ThemeContext';

// Track if animations have played in this session (module-level to persist across remounts)
let hasAnimatedInSession = false;

// Card component matching GameOptionsPage design - defined outside to prevent recreation
const Card = React.memo(({ children, className = '', delay = 0, index = 0, shouldAnimate = true }) => {
  const [isHovered, setIsHovered] = useState(false);
  const hasAnimatedRef = React.useRef(!shouldAnimate);
  const { isDarkMode } = useContext(ThemeContext);
  const hoverColor = isDarkMode ? '#ff9e42' : '#06bae4';
  
  // Mark animation as complete after it finishes
  React.useEffect(() => {
    if (shouldAnimate && !hasAnimatedRef.current) {
      const timer = setTimeout(() => {
        hasAnimatedRef.current = true;
      }, (delay + 0.6) * 1000);
      return () => clearTimeout(timer);
    }
  }, [shouldAnimate, delay]);
  
  const hexToRgba = (hex, alpha) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };
  
  return (
    <motion.div
      initial={hasAnimatedRef.current || !shouldAnimate ? false : { opacity: 0, y: 50, scale: 0.9 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={hasAnimatedRef.current || !shouldAnimate ? { duration: 0 } : {
        duration: 0.6,
        delay: delay,
        type: "spring",
        stiffness: 100,
      }}
      whileHover={{
        scale: 1.02,
        y: -5,
        transition: { duration: 0.2 },
      }}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
      className={`relative backdrop-blur-xl bg-white/80 dark:bg-gray-800/80 rounded-3xl shadow-2xl p-6 border transition-all duration-300 ${className}`}
      style={{
        boxShadow: isHovered
          ? `0 20px 60px ${hexToRgba(hoverColor, 0.25)}, 0 0 40px ${hexToRgba(hoverColor, 0.2)}`
          : '0 10px 40px rgba(0, 0, 0, 0.1)',
        borderColor: isHovered 
          ? hexToRgba(hoverColor, 0.4)
          : isDarkMode ? 'rgba(55, 65, 81, 0.5)' : 'rgba(255, 255, 255, 0.2)',
      }}
    >
      <motion.div
        className="absolute inset-0 rounded-3xl opacity-0 pointer-events-none"
        animate={{
          opacity: isHovered ? 0.2 : 0,
        }}
        transition={{ duration: 0.3 }}
        style={{
          background: `linear-gradient(135deg, ${hexToRgba(hoverColor, 0.25)}, ${hexToRgba(hoverColor, 0.4)})`,
          filter: 'blur(20px)',
          zIndex: -1,
        }}
      />
      {children}
    </motion.div>
  );
});
Card.displayName = 'Card';

const MultiplayerWaitingRoom = () => {
  const { roomId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [room, setRoom] = useState(null);
  const [players, setPlayers] = useState([]);
  const [adminId, setAdminId] = useState(null);
  const [error, setError] = useState('');
  const [subject, setSubject] = useState('');
  const [difficulty, setDifficulty] = useState('');
  const [isConnected, setIsConnected] = useState(false);
  const [preGameCountdown, setPreGameCountdown] = useState(0);
  const preCountdownRef = useRef(0);
  const [capacity, setCapacity] = useState(0);
  const [roomName, setRoomName] = useState('');
  const [roomPassword, setRoomPassword] = useState(location.state?.roomPassword || '');
  const [isPrivate, setIsPrivate] = useState(false);
  const [passwordPrompt, setPasswordPrompt] = useState(false);
  const [enteredPassword, setEnteredPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [hasEnteredPassword, setHasEnteredPassword] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [roomError, setRoomError] = useState('');
  const [roomNotFound, setRoomNotFound] = useState(false);
  const [roomFull, setRoomFull] = useState(false);
  const [alreadyJoined, setAlreadyJoined] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showEndSessionModal, setShowEndSessionModal] = useState(false); //copy EndSessionModal from PlayGamePage.js
  // Invite modal state
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [copySuccess, setCopySuccess] = useState('');
  const [shareMessage, setShareMessage] = useState('');
  const [toolTipTextCopyText, setToolTipTextCopyText] = useState('Copy text');
  const [toolTipTextCopyLink, setToolTipTextCopyLink] = useState('Copy link');
  const [modalVisible, setModalVisible] = useState(false);
  const [backdropVisible, setBackdropVisible] = useState(false);
  const [showNoQuestionsModal, setShowNoQuestionsModal] = useState(false);
  const [noQuestionsError, setNoQuestionsError] = useState('');
  const [isWaitingForNextRound, setIsWaitingForNextRound] = useState(false);
  const isWaitingForRound = useRef(false);
  const initialStatusReceived = useRef(false);
  const roomContainerRef = useRef(null);
  const inactivityTimerRef = useRef(null);
  const isLeavingRef = useRef(false);
  // Construct room link for sharing
  const roomLink = `${window.location.origin}/multiplayer/waiting-room/${roomId}`;
  const { isDarkMode } = useContext(ThemeContext);
  
  // Animation state tracking to prevent re-animations
  const hasAnimatedRef = useRef(hasAnimatedInSession);
  const [shouldAnimateCards, setShouldAnimateCards] = useState(!hasAnimatedInSession);
  
  // Memoize gradient for title and section headings
  const titleGradient = useMemo(() => {
    const color = isDarkMode ? '#ff7a00' : '#212D82';
    return `linear-gradient(90deg, ${color} 0%, ${color} 40%, rgba(255, 122, 0, 0.8) 50%, ${color} 60%, ${color} 100%)`;
  }, [isDarkMode]);

  const sectionHeadingGradient = useMemo(() => {
    const color = isDarkMode ? '#ff7a00' : '#212D82';
    return `linear-gradient(90deg, ${color} 0%, ${color} 40%, rgba(255, 122, 0, 0.8) 50%, ${color} 60%, ${color} 100%)`;
  }, [isDarkMode]);
  
  // Mark animations as started and complete after they finish
  useEffect(() => {
    if (!hasAnimatedInSession && !hasAnimatedRef.current) {
      hasAnimatedRef.current = true;
      const timer = setTimeout(() => {
        hasAnimatedInSession = true;
        setShouldAnimateCards(false);
      }, 2500);
      return () => clearTimeout(timer);
    }
  }, []);

  const userStr = typeof window !== 'undefined' ? localStorage.getItem('sat_user') : null;
  const user = userStr ? JSON.parse(userStr) : null;
  const playerName = useMemo(() => {
    if (!user) return 'Player';
    // Use screen_name as the primary choice, fallback to other names
    const nameCandidates = [user.screenName, user.screen_name, user.user_name, user.username, user.name];
    const chosen = nameCandidates.find(Boolean);
    if (chosen) return chosen;
    if (user.email && typeof user.email === 'string') {
      const local = user.email.split('@')[0];
      if (local) return local;
    }
    return 'Player';
  }, [user]);
  const addRoomMember = async () => {
    if (isLeavingRef.current) return;
    const add_member = await api.post(`/api/compete/add_room_member/`, {
      room_id: roomId,
    });
    console.log('add_member success', add_member);
  }
  const removeRoomMember = async () => {
    try {
      const remove_member = await api.post(`/api/compete/remove_room_member/`, {
        room_id: roomId,
      });
      console.log('remove_member success', remove_member);
    } catch (error) {
      console.error('Failed to remove room member:', error);
    }
  }
  const updateRoomMember = async (isHost,roomId,adminId) => {
    const update_member = await api.post(`/api/compete/update_room_member/`, {
      room_id: roomId,
      is_host: isHost,
      score: 0,
      rank_in_room: 0,
    });
    console.log('update_member success', update_member);
  }
  const updateRoom = async (adminId,roomId,status) => {
    try {
      const update_room = await api.post(`/api/compete/update_room/`, {
        room_id: roomId,
        status: status,
        started_at: null,
        ended_at: null,
        winner_user: null,
      });
      console.log('update_room success', update_room);
    } catch (error) {
      console.error('Failed to update room:', error);
    }
  }

  const handlePasswordSubmit = async () => {
    if (enteredPassword.trim() === '') {
      setPasswordError('Please enter a password');
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_COLYSEUS_URL}/rooms/${encodeURIComponent(roomId)}/validate`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ password: enteredPassword }),
        },
      );
      const result = await response.json().catch(() => ({}));
      if (!response.ok || !result.ok) {
        setPasswordError('Incorrect password. Please try again.');
        return;
      }

      setRoomPassword(enteredPassword);
      setPasswordError('');
      setHasEnteredPassword(true);
      setPasswordPrompt(false);
    } catch {
      setPasswordError('Unable to validate the password. Please try again.');
    }
  };

  const handlePasswordCancel = () => {
    navigate('/lobby-multiplayer', {
      replace: true,
      state: { error: 'Access denied. Incorrect password for private room.' }
    });
  };

  const handleBackToLobby = () => {
    navigate('/lobby-multiplayer', { replace: true });
  };

  useEffect(() => {
    // Show exit confirmation message when player trys to close tab
    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = '';
      return event.returnValue;
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  useEffect(() => {
    // Show exit confirmation when player clicks outside of game room container.
    const handleOutsideClick = (event) => {
      if (
        roomContainerRef.current &&
        !roomContainerRef.current.contains(event.target) &&
        !showEndSessionModal &&
        !showInviteModal
      ) {
        setShowEndSessionModal(true);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [showEndSessionModal, showInviteModal]);
  useEffect(() => {
    // show exit confirmation when player is inactive for certain time.
   const handleInactivity = () => {
        setShowEndSessionModal(true);

    };

    const resetInactivityTimer = () => {
        clearTimeout(inactivityTimerRef.current);
        // Set the timer to temporary duration 60 mins (60000 milliseconds)
        inactivityTimerRef.current = setTimeout(handleInactivity, 60000);
    };

    const events = ['mousemove', 'keydown', 'click', 'scroll'];

    const addListeners = () => {
        events.forEach(event => window.addEventListener(event, resetInactivityTimer));
        resetInactivityTimer(); // Clear timer
    };

    const removeListeners = () => {
        events.forEach(event => window.removeEventListener(event, resetInactivityTimer));
        clearTimeout(inactivityTimerRef.current);
    };

    if (isConnected) {
        addListeners();
    } else {
        removeListeners();
    }

    return () => {
        removeListeners();
    };

  }, [isConnected]);
  // Define connect function outside useEffect so it can be reused
  const connect = async () => {
    console.log('connect');

    if (isLeavingRef.current) {
      console.log('Skipping connect because leaving');
      return;
    }

    // Prevent duplicate connections
    if (isConnecting || isConnected || alreadyJoined) {
      console.log('Already connecting, connected, or already joined, skipping');
      return;
    }

    // For private rooms, check if password has been entered correctly or verified in lobby
    const { fromLobby, passwordVerified } = location.state || {};
    const isPasswordValid = hasEnteredPassword || (fromLobby && passwordVerified);

    if (isPrivate && !isPasswordValid) {
      console.log('Private room - password required');
      return;
    }

    setIsConnecting(true);
    try {
      console.log('connecting');
      
      // Check if player has previous score in this room
      let previousScore = 0;
      let previousCorrectAnswers = 0;
      let previousTotalAnswers = 0;
      
      if (user?.user_id) {
        try {
          const roomMemberResponse = await api.get(`/api/compete/get_room_member/${roomId}/`);
          if (roomMemberResponse.data?.room_member) {
            const roomMember = roomMemberResponse.data.room_member;
            previousScore = roomMember.score || 0;
            previousCorrectAnswers = roomMember.correct_answers || 0;
            previousTotalAnswers = roomMember.total_answers || 0;
            console.log('Found previous room member data:', {
              score: previousScore,
              correctAnswers: previousCorrectAnswers,
              totalAnswers: previousTotalAnswers
            });
          }
          else {
            console.log('No previous room member data found, adding new room member');
            addRoomMember();
          }
        } catch (error) {
          console.log('No previous room member data found or error:', error);
          // Continue with default values (0)
        }
      }
      
      const client = new Client(process.env.REACT_APP_COLYSEUS_WS_URL);
      const authToken = await getAuthIdToken();
      if (!authToken) throw new Error('Your session has expired. Sign in again.');
      const joined = await client.joinById(roomId, {
        playerName,
        authToken,
        password: isPrivate ? (enteredPassword || roomPassword) : undefined,
        previousScore: previousScore,
        previousCorrectAnswers: previousCorrectAnswers,
        previousTotalAnswers: previousTotalAnswers
      });
      console.log('joined', joined);
      setRoom(joined);
      setIsConnected(true);
      setIsConnecting(false);

      // Persist connection for hand-off to game page
      try {
        window.__colyseusClient = client;
        window.__colyseusRoom = joined;
        window.__colyseusRoomId = roomId;
      } catch {}

      // Add message listeners
      joined.onMessage('welcome', (data) => {
        initialStatusReceived.current = true;
        if (data.gameInProgress) {
          setIsWaitingForNextRound(true);
          isWaitingForRound.current = true;
        }
      });

      joined.onMessage('nextRoundReady', () => {
        // Reset the waiting flag before navigating
        isWaitingForRound.current = false;
        // Automatically navigate to the game room
        navigate(`/multiplayer/game/${encodeURIComponent(roomId)}`, {
          replace: true,
          state: {
            fromWaitingRoom: true,
            canAccessGameRoom: true,
            roomId: roomId,
            adminId: adminId,
            isHost: isAdmin,
            roomPassword,
          }
        });
      });

      // observe state players (include userId for admin identification)
      joined.onStateChange((state) => {
        const list = [];
        try {
          if (state && state.players && typeof state.players.forEach === 'function') {
            state.players.forEach((p, id) => {
              list.push({ id, name: p.name, score: p.score, userId: p.userId });
            });
          }
        } catch {}
        setPlayers(list);
        // Fallback: if game or betting has started and for any reason we missed 'gameStarted', navigate to game
        try {
          const active = !!(state && (state.gameActive));
          console.log('active', active);
          console.log('isWaitingForRound.current', isWaitingForRound.current);
          if (active && initialStatusReceived.current && !isWaitingForRound.current) {
            console.log('adminIdqwe', isAdmin);
            navigate(`/multiplayer/game/${encodeURIComponent(roomId)}`, {
              replace: true,
              state: {
                canAccessGameRoom: true,
                fromWaitingRoom: true,
                roomId: roomId,
                roomPassword,
              }
            });
          }
        } catch {}
      });

      joined.onMessage('adminChanged', (data) => {
          if (data && data.adminId != null) {
            setAdminId(String(data.adminId));
            updateRoomMember(true,roomId,data.adminId);
            updateRoom(data.adminId,roomId,'waiting');
          } else {
            setAdminId(null);
            updateRoomMember(false,roomId,data.adminId);
          }
      });

      // Navigate to game page while keeping the same room connection
      joined.onMessage('gameStarted', async () => {
        console.log('adminIdqwe123', isAdmin);
        navigate(`/multiplayer/game/${encodeURIComponent(roomId)}`, {
          replace: true,
          state: {
            canAccessGameRoom: true,
            fromWaitingRoom: true,
            roomId: roomId,
            roomPassword,
          }
        });
      });

      // If host broadcasts PREPARE_COUNTDOWN, start local countdown for everyone
      joined.onMessage('chatMessage', (payload) => {
        if (payload && typeof payload.message === 'string') {
          if (payload.message === 'PREPARE_COUNTDOWN') {
            preCountdownRef.current =5;
            setPreGameCountdown(5);
          }
        }
      });

      // Handle game errors (like no questions available)
      joined.onMessage('gameError', (data) => {
        console.log('Game error received:', data);
        setNoQuestionsError(
          data?.message || data?.error || 'The game could not be started. Please try again.'
        );
        setShowNoQuestionsModal(true);
      });

      // Handle game abandoned (all active players left while waiting)
      joined.onMessage('gameAbandoned', async (data) => {
        console.log('Game abandoned received:', data);
        const message = data.message || 'All active players have left the game. Returning to lobby.';
        try {
          await joined.leave();
        } catch (err) {
          console.error('Error leaving room:', err);
        }
        await removeRoomMember();
        navigate('/lobby-multiplayer', {
          replace: true,
          state: { error: message }
        });
      });

      // Remove player immediately on explicit leave broadcast
      joined.onMessage('playerLeft', (payload) => {
        try {
          if (payload && (payload.playerId || payload.userId)) {
            setPlayers((prev) => prev.filter((p) => {
              if (payload.playerId && p.id === payload.playerId) return false;
              if (payload.userId && String(p.userId) === String(payload.userId)) return false;
              return true;
            }));
          }
        } catch {}
      });

      // Add player immediately when we receive explicit join broadcast
      joined.onMessage('playerJoined', (payload) => {
        try {
          if (payload && payload.playerId) {
            setPlayers((prev) => {
              if (prev.some(p => p.id === payload.playerId)) return prev;
              return [...prev, { id: payload.playerId, name: payload.playerName || 'Player' }];
            });
          }
        } catch {}
      });

      joined.onLeave(() => {
        setIsConnected(false);
        setIsConnecting(false);
      });
    } catch (e) {
      setError(e.message || 'Failed to join room');
      setIsConnecting(false);
    }
  };

  useEffect(() => {
    let active = true;

    const preload = async () => {
      console.log('preload', roomId);
      try {
        console.log('preload');

        const res = await fetch(`${process.env.REACT_APP_COLYSEUS_URL}/rooms/${encodeURIComponent(roomId)}`);
        console.log('res', res);
        if (res.ok) {
          const data = await res.json();
          if (!active) return;

          // Clear any previous errors and set loading to false
          setRoomError('');
          setRoomNotFound(false);
          setRoomFull(false);
          setAlreadyJoined(false);
          setIsLoading(false);

          if (data?.roomName) setRoomName(data.roomName);
          if (data && data.subject) setSubject(data.subject);
          if (data && data.difficulty) setDifficulty(data.difficulty);
          if (data && data.adminId) setAdminId(data.adminId);
          if (typeof data?.maxClients === 'number') setCapacity(data.maxClients);

          // Check if user is already in the room
          if (user?.user_id && data?.players) {
            const isUserInRoom = data.players.some(player =>
              player.userId === user.user_id || player.id === user.user_id
            );
            if (isUserInRoom) {
              setAlreadyJoined(true);
              setRoomError('You are already in this room. Please check your other tabs or windows.');
              setIsLoading(false);
              return false;
            }
          }

          if (data?.visibility === 'private') {
            setIsPrivate(true);

            // Check if user came from lobby with password verification
            const { fromLobby, passwordVerified } = location.state || {};
            if (fromLobby && passwordVerified) {
              // User already verified password in lobby, skip prompt
              setHasEnteredPassword(true);
              setPasswordPrompt(false);
            } else {
              // Show password prompt for direct access to private rooms
              setPasswordPrompt(true);
            }
          }
          // if room is completed, show error message
          if (data?.status && String(data.status) === 'completed') {
            setRoomError('Room is locked. Game already completed.');
            setIsLoading(false);
            return false;
          }
          // if room is in progress, allow joining but show info message
          if (data?.status && String(data.status) === 'in_progress') {
            console.log('Room is in progress, allowing join with waiting state');
            // Continue with join process - player will be put in waiting state
          }
          // if room is already full, show error message
          const current = Array.isArray(data?.players) ? data.players.length : (data?.clients ?? 0);
          const max = data?.maxClients ?? 0;
          if (max > 0 && current >= max) {
            setRoomFull(true);
            setRoomError('Room is full. Cannot join at this time.');
            setIsLoading(false);
            return false;
          }
          return true;
        } else if (res.status === 404) {
          // Room not found
          setRoomNotFound(true);
          setRoomError('The room you are looking for does not exist.');
          setIsLoading(false);
          return false;
        } else {
          // Other error
          setRoomError('Failed to load room information. Please try again.');
          setIsLoading(false);
          return false;
        }
      } catch (error) {
        console.log('preload error', error);
        setRoomError('Failed to load room information. Please check your connection and try again.');
        setIsLoading(false);
        return false;
      }
      return false;
    };

    (async () => {
      // No longer restricting based on points - anyone can create/join rooms
      console.log('preload in useEffect');
      const ok = await preload();
      if (ok === false) return;

      // Do not auto-connect here. Connection will be handled by the separate effect
      // once privacy/password state is settled to avoid joining private rooms early.
      // The below block is removed as it causes a race condition for private rooms
      /*
      if ((!isPrivate && !isConnecting && !isConnected && !alreadyJoined)) {
        await connect();
      }
      */
    })();
    return () => { active = false; try { room && room.leave(); } catch {} };
  }, [roomId]);

  // Separate useEffect to handle connection once privacy/password state is settled
  useEffect(() => {
    // Don't do anything until preload has run (indicated by isLoading being false)
    if (isLoading) {
      return;
    }
    if (isLeavingRef.current) {
      return;
    }

    const { fromLobby, passwordVerified } = location.state || {};
    const isPasswordValid = hasEnteredPassword || (fromLobby && passwordVerified);

    // Determine if we should connect:
    // - Public rooms should connect immediately after preload.
    // - Private rooms should connect only after password validation.
    const canConnect = (!isPrivate) || (isPrivate && isPasswordValid);

    if (canConnect && !isConnected && !isConnecting && !alreadyJoined) {
      connect();
    }
  }, [isLoading, isPrivate, hasEnteredPassword, isConnected, isConnecting, alreadyJoined, location.state]);

  const isAdmin = useMemo(() => {
    // We can't know our session id easily here without metadata; rely on adminId compare when available
    // For now, consider creator as admin; adminId comes from metadata (user_id)
    return adminId && user?.user_id && String(adminId) === String(user.user_id);
  }, [adminId, user]);

  // Generate share message
  const generateShareMessage = () => {
    const playerName = user?.screenName || user?.screen_name || user?.user_name || user?.username || user?.name || 'A friend';
    let message = `${playerName} is inviting you to play a SAT game!\n\n`;
    message += `🏆 Room: ${roomName}\n`;
    message += `🏷️ Room Code: ${roomId}\n`;
    message += `📚 Subject: ${subject || 'Mixed Topics'}\n`;
    message += `🧠 Difficulty: ${difficulty || 'Easy'}\n`;
    if (isPrivate && roomPassword) {
      message += `🔐 Password: ${roomPassword}\n`;
    }
    message += `\n👉 Join here: ${roomLink}`;
    return message;
  };

  // Copy share text to clipboard
  const handleCopyShare = async () => {
    try {
      await navigator.clipboard.writeText(shareMessage);
      setToolTipTextCopyText('Copied!');
      setTimeout(() => setToolTipTextCopyText('Copy text'), 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  // Copy link to clipboard
  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(roomLink);
      setToolTipTextCopyLink('Copied!');
      setTimeout(() => setToolTipTextCopyLink('Copy link'), 2000);
    } catch (err) {
      console.error('Failed to copy link: ', err);
    }
  };

  // Update share message when modal opens
  useEffect(() => {
    if (showInviteModal) {
      setShareMessage(generateShareMessage());
    }
  }, [showInviteModal, roomName, subject, isPrivate, roomPassword, user]);

  // Handle modal animation - use refs to prevent re-animation
  const modalHasAnimatedRef = useRef(false);
  
  const openModal = () => {
    if (!modalHasAnimatedRef.current) {
      modalHasAnimatedRef.current = true;
    }
    setShowInviteModal(true);
    setBackdropVisible(true);
    // Small delay to ensure backdrop is rendered first
    setTimeout(() => {
      setModalVisible(true);
    }, 10);
  };

  const closeModal = () => {
    setModalVisible(false);
    setBackdropVisible(false);
    // Wait for animation to complete before hiding modal
    setTimeout(() => {
      setShowInviteModal(false);
    }, 300);
  };

  const adminName = useMemo(() => {
    if (!adminId) return '';
    const p = players.find(pl => String(pl.userId) === String(adminId));
    return p?.name || '';
  }, [players, adminId]);

  const canStart = players.length > 1;

  const handleLeave = async () => {
    isLeavingRef.current = true;
    try { await room?.leave(); } catch {}
    removeRoomMember();
    console.log('players', players);
    if (players.length === 1) {
      updateRoom(null, roomId, 'archived');
    }
    navigate('/lobby-multiplayer');
  };

  const handleStart = () => {
    try {
      room?.send('chatMessage', { message: 'PREPARE_COUNTDOWN' });
      preCountdownRef.current = 5;
      setPreGameCountdown(5);
    } catch {}
  };

  // Tick countdown; when it ends, only admin triggers startGame
  useEffect(() => {
    if (preGameCountdown <= 0) return;
    const t = setInterval(() => {
      preCountdownRef.current = Math.max(0, preCountdownRef.current - 1);
      setPreGameCountdown(preCountdownRef.current);
      if (preCountdownRef.current === 0) {
        if (isAdmin && room) {
          try { room.send('startGame'); } catch {}
        }
      }
    }, 1000);
    return () => clearInterval(t);
  }, [preGameCountdown, isAdmin, room]);

  // Build 8 slots for the avatar grid
  const slots = Array.from({ length: capacity || 8 });
  const playerByIndex = players.slice(0, slots.length);

  // Show loading screen while checking room
  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col relative pb-24 pl-0 md:pl-24 overflow-hidden bg-gradient-to-br from-sky-50 to-orange-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 font-zonaProRegular">
        <div className="relative z-20 px-4 sm:px-6 lg:px-8 py-6 flex items-center justify-center min-h-screen">
          <Card delay={0.1} shouldAnimate={shouldAnimateCards}>
            <div className="text-center">
              <div className="text-6xl mb-4">⏳</div>
              <h1
                key={`loading-room-${isDarkMode}`}
                className="text-2xl sm:text-3xl md:text-4xl font-bold font-zonaProUltra mb-6 relative inline-block"
                style={{
                  color: isDarkMode ? '#ff7a00' : '#212D82',
                  background: sectionHeadingGradient,
                  backgroundSize: '200% 100%',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  animation: 'shine 10s linear infinite',
                  lineHeight: '1.2',
                  paddingBottom: '0.25rem'
                }}
              >
                Loading Room
              </h1>
              <div className="neon-divider my-4"></div>
              <p className="text-base sm:text-lg md:text-xl text-gray-600 dark:text-gray-400 text-center mb-6 font-zonaProRegular">
                Checking room information...
              </p>
              <div className="flex justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500 dark:border-orange-500"></div>
              </div>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  if (isWaitingForNextRound) {
    return (
      <div className="min-h-screen flex flex-col relative pb-24 pl-0 md:pl-24 overflow-hidden bg-gradient-to-br from-sky-50 to-orange-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 font-zonaProRegular">
        <div className="relative z-20 px-4 sm:px-6 lg:px-8 py-6 flex items-center justify-center min-h-screen">
          <Card delay={0.1} shouldAnimate={shouldAnimateCards}>
            <div className="text-center">
              <h2
                key={`game-in-progress-${isDarkMode}`}
                className="text-2xl sm:text-3xl md:text-4xl font-bold font-zonaProUltra mb-4 relative inline-block"
                style={{
                  color: isDarkMode ? '#ff7a00' : '#212D82',
                  background: sectionHeadingGradient,
                  backgroundSize: '200% 100%',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  animation: 'shine 10s linear infinite',
                  lineHeight: '1.2',
                  paddingBottom: '0.25rem'
                }}
              >
                Game in Progress
              </h2>
              <div className="flex items-center justify-center space-x-3 my-4">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500 dark:border-orange-500"></div>
                <p className="text-base sm:text-lg md:text-xl text-gray-600 dark:text-gray-400 font-zonaProRegular">
                  Please wait for the current round to finish.
                </p>
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500 dark:border-orange-500"></div>
              </div>
              <p className="text-base sm:text-lg text-gray-500 dark:text-gray-400 mt-2 font-zonaProRegular">You will be automatically joined to the next round.</p>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  // Password prompt for private rooms
  if (passwordPrompt) {
    return (
      <div className="min-h-screen flex flex-col relative pb-24 pl-0 md:pl-24 overflow-hidden bg-gradient-to-br from-sky-50 to-orange-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 font-zonaProRegular">
        <div className="relative z-20 px-4 sm:px-6 lg:px-8 py-6 flex items-center justify-center min-h-screen">
          <Card delay={0.1} shouldAnimate={shouldAnimateCards} className="w-full max-w-md">
            <h1
              key={`private-room-${isDarkMode}`}
              className="text-2xl sm:text-3xl md:text-4xl font-bold font-zonaProUltra text-center mb-6 relative inline-block"
              style={{
                color: isDarkMode ? '#ff7a00' : '#212D82',
                background: sectionHeadingGradient,
                backgroundSize: '200% 100%',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                animation: 'shine 10s linear infinite',
                lineHeight: '1.2',
                paddingBottom: '0.25rem'
              }}
            >
              Private Room
            </h1>
            <div className="neon-divider my-4"></div>
            <p className="text-base sm:text-lg md:text-xl text-gray-600 dark:text-gray-400 text-center mb-6 font-zonaProRegular">
              This room is private. Please enter the password to join.
            </p>
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-gray-600 dark:text-gray-400 mb-2">Room Password</label>
                <input
                  type="password"
                  value={enteredPassword}
                  onChange={(e) => setEnteredPassword(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handlePasswordSubmit()}
                  placeholder="Enter room password"
                  className="w-full px-4 py-3 rounded-lg bg-transparent border border-gray-300 dark:border-gray-600 text-gray-800 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500 dark:focus:ring-orange-500"
                  autoFocus
                />
              </div>
              {passwordError && (
                <div className="text-red-600 dark:text-red-400 text-sm text-center">{passwordError}</div>
              )}
              <div className="flex gap-3">
                <motion.button
                  type="button"
                  onClick={handlePasswordCancel}
                  className="flex-1 py-3 px-6 bg-red-600 hover:bg-red-700 text-white text-base sm:text-lg md:text-xl font-zonaProUltra rounded-full transition-colors duration-150"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Cancel
                </motion.button>
                <motion.button
                  type="button"
                  onClick={handlePasswordSubmit}
                  className="flex-1 py-3 px-6 bg-black dark:bg-gray-700 hover:bg-gray-800 dark:hover:bg-gray-600 text-white text-base sm:text-lg md:text-xl font-zonaProUltra rounded-full transition-colors duration-150"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Join Room
                </motion.button>
              </div>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  // Show error message for room issues
  if (roomError) {
    return (
      <div className="min-h-screen flex flex-col relative pb-24 pl-0 md:pl-24 overflow-hidden bg-gradient-to-br from-sky-50 to-orange-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 font-zonaProRegular">
        <div className="relative z-20 px-4 sm:px-6 lg:px-8 py-6 flex items-center justify-center min-h-screen">
          <Card delay={0.1} shouldAnimate={shouldAnimateCards} className="w-full max-w-md">
            <div className="text-center">
              <div className="text-6xl mb-4">
                {roomNotFound ? '🔍' : roomFull ? '🚫' : alreadyJoined ? '👤' : '⚠️'}
              </div>
              <h1
                key={`room-error-${isDarkMode}-${roomNotFound}-${roomFull}-${alreadyJoined}`}
                className="text-2xl sm:text-3xl md:text-4xl font-bold font-zonaProUltra mb-6 relative inline-block"
                style={{
                  color: isDarkMode ? '#ff7a00' : '#212D82',
                  background: sectionHeadingGradient,
                  backgroundSize: '200% 100%',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  animation: 'shine 10s linear infinite',
                  lineHeight: '1.2',
                  paddingBottom: '0.25rem'
                }}
              >
                {roomNotFound ? 'Room Not Found' : roomFull ? 'Room Full' : alreadyJoined ? 'Already Joined' : 'Room Error'}
              </h1>
              <div className="neon-divider my-4"></div>
              <p className="text-base sm:text-lg md:text-xl text-gray-600 dark:text-gray-400 text-center mb-6 font-zonaProRegular">
                {roomError}
              </p>
              {roomNotFound && (
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
                  Room Code: <span className="font-mono bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">{roomId}</span>
                </p>
              )}
              {alreadyJoined && (
                <div className="text-sm text-gray-500 dark:text-gray-400 mb-6 space-y-2">
                  <p>You may already have this room open in another tab or window.</p>
                  <p>Room Code: <span className="font-mono bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">{roomId}</span></p>
                </div>
              )}
              <motion.button
                type="button"
                onClick={handleBackToLobby}
                className="w-full py-3 px-6 bg-black dark:bg-gray-700 hover:bg-gray-800 dark:hover:bg-gray-600 text-white text-base sm:text-lg md:text-xl font-zonaProUltra rounded-full transition-colors duration-150"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                Back to Lobby
              </motion.button>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div ref={roomContainerRef} className="min-h-screen flex flex-col relative pb-24 pl-0 md:pl-24 overflow-hidden bg-gradient-to-br from-sky-50 to-orange-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 font-zonaProRegular">
      <div className="relative z-20 px-4 sm:px-6 lg:px-8 py-6">

        {/* Animated Header */}
        <motion.div
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, type: "spring" }}
          className="text-center mb-8"
        >
          <h1
            key={`waiting-room-title-${isDarkMode}`}
            className="text-5xl sm:text-6xl lg:text-7xl font-bold font-zonaProUltra mb-4 relative inline-block"
            style={{
              color: isDarkMode ? '#ff7a00' : '#212D82',
              background: titleGradient,
              backgroundSize: '200% 100%',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              animation: 'shine 10s linear infinite',
              lineHeight: '1.2',
              paddingBottom: '0.25rem'
            }}
          >
            Waiting Room
          </h1>
          <motion.div
            className="text-base sm:text-lg md:text-xl text-gray-600 dark:text-gray-400 font-zonaProRegular space-y-2"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            <div>Room Code: <span className="font-semibold">{roomId}</span></div>
            {subject && (
              <div className="sm:inline sm:before:content-['•'] sm:before:mx-2">
                Subject: <span className="font-semibold">{subject}</span>
              </div>
            )}
          </motion.div>
        </motion.div>

      <div className="w-full max-w-7xl mx-auto">
        <Card delay={0.1} shouldAnimate={shouldAnimateCards}>
          {/* Header: room name and subject */}
          <div className="mb-4">
          <div className="md:hidden space-y-4 pb-4 border-b border-gray-200 dark:border-gray-700">
            <div className="grid grid-cols-2 gap-6">
              <div className="text-center">
                <span className="text-gray-500 dark:text-gray-400 text-xs uppercase tracking-wider">Room</span>
                <h2 className="text-xl sm:text-2xl font-zonaProUltra text-cyan-500 dark:text-orange-500">{roomName || 'Waiting Room'}</h2>
              </div>
              <div className="text-center">
                <span className="text-gray-500 dark:text-gray-400 text-xs uppercase tracking-wider">Difficulty</span>
                <p className="text-xl sm:text-2xl font-bold text-gray-700 dark:text-gray-300">{difficulty || 'Easy'}</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-6">
              {roomPassword && (
                <div className="text-center">
                  <span className="text-gray-500 dark:text-gray-400 text-xs uppercase tracking-wider">Password</span>
                  <p className="text-xl sm:text-2xl font-bold text-gray-700 dark:text-gray-300 break-words">{roomPassword}</p>
                </div>
              )}

              <div className="text-center">
                <span className="text-gray-500 dark:text-gray-400 text-xs uppercase tracking-wider">Players</span>
                <p className="text-xl sm:text-2xl font-bold text-green-500 dark:text-green-400">{players.length}/{capacity || 8}</p>
              </div>
              <div className="text-center">
                <span className="text-gray-500 dark:text-gray-400 text-xs uppercase tracking-wider">Admin</span>
                <p className="text-xl sm:text-2xl font-bold text-cyan-500 dark:text-orange-500 break-words">
                  {adminName || 'TBD'} {isAdmin ? '(You)' : ''}
                </p>
              </div>
              <div className="text-center">
                <span className="text-gray-500 dark:text-gray-400 text-xs uppercase tracking-wider">Status</span>
                <div className="flex items-center justify-center mt-1 relative group">
                  <div
                    className={`w-3 h-3 rounded-full ${isConnected ? 'bg-green-500 dark:bg-green-400' : 'bg-red-500 dark:bg-red-400'} shadow-lg`}
                    title={isConnected ? 'Connected' : 'Disconnected'}
                  />
                  <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 dark:bg-gray-700 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                    {isConnected ? 'Connected' : 'Disconnected'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {preGameCountdown > 0 && (
            <div className="fixed inset-0 z-[9999] flex items-center justify-center backdrop-blur-md">
              <div className="bg-transparent px-4 sm:px-6 md:px-10 py-8 text-center w-full h-full flex items-center justify-center">
                <p
                  className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold font-zonaProUltra relative inline-block"
                  style={{
                    color: '#06bae4'
                  }}
                >
                  Game starts in {preGameCountdown}s
                </p>
              </div>
            </div>
          )}
          {error && <div className="mt-3 text-red-600 dark:text-red-400 text-sm">{error}</div>}
          </div>
          <div className="hidden md:flex md:items-center md:justify-between gap-4 pb-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center space-x-6 mt-2 md:mt-0">
              <div className="text-center">
                <span className="text-gray-500 dark:text-gray-400 text-sm uppercase tracking-wider">Room</span>
                <h1 className="text-xl sm:text-2xl font-zonaProUltra text-cyan-500 dark:text-orange-500">{roomName || 'Waiting Room'}</h1>
              </div>
              <div className="text-center">
                <span className="text-gray-500 dark:text-gray-400 text-sm uppercase tracking-wider">Difficulty</span>
                <p className="text-xl sm:text-2xl font-bold text-gray-700 dark:text-gray-300">{difficulty || 'Easy'}</p>
              </div>
            </div>
            <div className="flex items-center space-x-6 mt-2 md:mt-0">
              {roomPassword && (
                <div className="text-center">
                  <span className="text-gray-500 dark:text-gray-400 text-sm uppercase tracking-wider">Password</span>
                  <p className="text-xl sm:text-2xl font-bold text-gray-700 dark:text-gray-300">{roomPassword}</p>
                </div>
              )}
              <div className="text-center">
                <span className="text-gray-500 dark:text-gray-400 text-sm uppercase tracking-wider">Players</span>
                <p className="text-xl sm:text-2xl font-bold text-green-500 dark:text-green-400">{players.length}/{capacity || 8}</p>
              </div>
              <div className="text-center">
                <span className="text-gray-500 dark:text-gray-400 text-sm uppercase tracking-wider">Admin</span>
                <p className="text-xl sm:text-2xl font-bold text-gray-800 dark:text-gray-200">{adminName || 'TBD'} {isAdmin ? '(You)' : ''}</p>
              </div>
              <div className="text-center">
                <span className="text-gray-500 dark:text-gray-400 text-sm uppercase tracking-wider">Status</span>
                <div className="flex items-center justify-center mt-1 relative group">
                  <div
                    className={`w-3 h-3 rounded-full ${isConnected ? 'bg-green-500 dark:bg-green-400' : 'bg-red-500 dark:bg-red-400'} shadow-lg`}
                    title={isConnected ? 'Connected' : 'Disconnected'}
                  />
                  <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 dark:bg-gray-700 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                    {isConnected ? 'Connected' : 'Disconnected'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Players avatar grid */}
          <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 md:grid-cols-4 gap-4">
            {slots.map((_, idx) => {
              const p = playerByIndex[idx];
              return (
                <motion.div
                  key={idx}
                  initial={shouldAnimateCards ? { opacity: 0, scale: 0.8 } : false}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.3 + idx * 0.1, duration: 0.5 }}
                  className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-xl rounded-2xl shadow-lg p-4 border border-gray-200 dark:border-gray-700 flex flex-col items-center justify-center min-h-[120px]"
                >
                  {p ? (
                    <>
                      <div className="h-14 w-14 rounded-full bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 flex items-center justify-center text-gray-700 dark:text-gray-200 font-bold">
                        {p.name?.[0]?.toUpperCase() || 'P'}
                      </div>
                      <div className="mt-2 text-gray-800 dark:text-gray-200 text-sm font-semibold truncate max-w-[120px]">{p.name}</div>
                    </>
                  ) : (
                    <>
                      <motion.button
                        type="button"
                        onClick={openModal}
                        className="h-14 w-14 rounded-full bg-slate-50 dark:bg-gray-700 border hover:bg-gray-200 dark:hover:bg-gray-600 border-gray-300 dark:border-gray-600 flex items-center justify-center text-gray-700 dark:text-gray-200 text-2xl"
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                      >
                        +
                      </motion.button>
                      <div className="mt-2 text-gray-500 dark:text-gray-400 text-sm">Invite</div>
                    </>
                  )}
                </motion.div>
              );
            })}
          </div>

          {/* Controls */}
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-end gap-3">
            <motion.button
              type="button"
              onClick={() => setShowEndSessionModal(true)}
              className="py-3 px-6 bg-red-600 hover:bg-red-700 text-white text-base sm:text-lg md:text-xl font-zonaProUltra rounded-full transition-colors duration-150"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              Leave Room
            </motion.button>
            <motion.button
              type="button"
              onClick={handleStart}
              disabled={!isAdmin || !canStart || preGameCountdown > 0}
              className={`py-3 px-6 bg-black dark:bg-gray-700 text-white text-base sm:text-lg md:text-xl font-zonaProUltra rounded-full hover:bg-gray-800 dark:hover:bg-gray-600 transition-colors duration-150 ${(!isAdmin || !canStart || preGameCountdown > 0) ? 'opacity-60 cursor-not-allowed' : ''}`}
              whileHover={(!isAdmin || !canStart || preGameCountdown > 0) ? {} : { scale: 1.02 }}
              whileTap={(!isAdmin || !canStart || preGameCountdown > 0) ? {} : { scale: 0.98 }}
            >
              Start Game
            </motion.button>
          </div>
        </Card>
      </div>
      </div>

       {/*End session confirmation modal */}
       <AnimatePresence>
        {showEndSessionModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center"
            onClick={() => setShowEndSessionModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="backdrop-blur-xl bg-white/90 dark:bg-gray-800/90 rounded-3xl shadow-2xl p-8 w-full max-w-md mx-4 border border-white/20 dark:border-gray-700/50"
            >
              <h2
                key={`leave-room-modal-${isDarkMode}`}
                className="text-2xl sm:text-3xl md:text-4xl font-bold font-zonaProUltra mb-4 relative inline-block"
                style={{
                  color: isDarkMode ? '#ff7a00' : '#212D82',
                  background: sectionHeadingGradient,
                  backgroundSize: '200% 100%',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  animation: 'shine 10s linear infinite',
                  lineHeight: '1.2',
                  paddingBottom: '0.25rem'
                }}
              >
                Leave Room
              </h2>
              <p className="text-base sm:text-lg md:text-xl text-gray-600 dark:text-gray-400 mb-6 font-zonaProRegular">
                Are you sure you want to leave the room? You will be redirected to the lobby.
              </p>
              <div className="flex justify-end space-x-4">
                <motion.button
                  type="button"
                  onClick={() => {
                    setShowEndSessionModal(false);
                  }}
                  className="px-4 py-2 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 rounded-full font-zonaProUltra text-gray-700 dark:text-gray-200 transition-colors"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Cancel
                </motion.button>
                <motion.button
                  type="button"
                  onClick={() => {
                    handleLeave();
                    setShowEndSessionModal(false);
                  }}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-full font-zonaProUltra transition-colors"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Leave Room
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      <AnimatePresence>
        {showInviteModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: backdropVisible ? 1 : 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={closeModal}
          >
            {/* Modal */}
            <motion.div
              initial={modalHasAnimatedRef.current ? false : { scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: modalVisible ? 1 : 0.9, opacity: modalVisible ? 1 : 0, y: modalVisible ? 0 : 20 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              transition={{ duration: 0.3 }}
              onClick={(e) => e.stopPropagation()}
              className="relative backdrop-blur-xl bg-white/90 dark:bg-gray-800/90 rounded-3xl shadow-2xl p-8 w-full max-w-2xl border border-white/20 dark:border-gray-700/50 max-h-[90vh] overflow-y-auto"
            >
              {/* Close button */}
              <motion.button
                type="button"
                onClick={closeModal}
                className="absolute top-6 right-6 z-10 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                whileHover={{ scale: 1.1, rotate: 90 }}
                whileTap={{ scale: 0.9 }}
              >
                <X className="text-xl" />
              </motion.button>

              <h2
                key={`invite-players-modal-${isDarkMode}`}
                className="text-2xl sm:text-3xl md:text-4xl font-bold text-center mb-6 relative inline-block w-full"
                style={{
                  color: isDarkMode ? '#ff7a00' : '#212D82',
                  background: sectionHeadingGradient,
                  backgroundSize: '200% 100%',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  animation: 'shine 10s linear infinite',
                  lineHeight: '1.2',
                  paddingBottom: '0.25rem'
                }}
              >
                Share Game Invite
              </h2>

              {/* Share message preview */}
              <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4 mb-6 border border-gray-200 dark:border-gray-600">
                <div className="text-sm font-semibold text-gray-600 dark:text-gray-400 mb-3 uppercase tracking-wider">Preview:</div>
                <div className="text-gray-800 dark:text-gray-200 whitespace-pre-line text-sm md:text-base leading-relaxed break-words">
                  {shareMessage}
                </div>
              </div>

              {/* Action buttons */}
              <div className="flex flex-wrap gap-3 justify-center mb-6">
                {/* Copy Text Button */}
                <motion.button
                  type="button"
                  onClick={handleCopyShare}
                  className="flex items-center gap-2 px-4 sm:px-6 py-2 sm:py-3 bg-blue-500 hover:bg-blue-600 dark:bg-blue-600 dark:hover:bg-blue-700 text-white text-sm sm:text-base md:text-lg rounded-full transition-colors font-zonaProUltra shadow-lg hover:shadow-xl"
                  title={toolTipTextCopyText}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  📋 Copy Text
                </motion.button>

                {/* Copy Link Button */}
                <motion.button
                  type="button"
                  onClick={handleCopyLink}
                  className="flex items-center gap-2 px-4 sm:px-6 py-2 sm:py-3 bg-green-500 hover:bg-green-600 dark:bg-green-600 dark:hover:bg-green-700 text-white text-sm sm:text-base md:text-lg rounded-full transition-colors font-zonaProUltra shadow-lg hover:shadow-xl"
                  title={toolTipTextCopyLink}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  🔗 Copy Link
                </motion.button>
              </div>

              {/* Social sharing buttons */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
                {/* WhatsApp */}
                <motion.a
                  href={`https://wa.me/?text=${encodeURIComponent(shareMessage)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex flex-col items-center p-3 sm:p-4 bg-white dark:bg-gray-700/50 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-xl transition-all duration-200 border border-gray-200 dark:border-gray-600 hover:border-green-300 dark:hover:border-green-700 shadow-md hover:shadow-lg"
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <img src={whatsappIcon} alt="WhatsApp" className="w-8 h-8 sm:w-10 sm:h-10 mb-1 sm:mb-2" />
                  <span className="text-xs sm:text-sm text-green-700 dark:text-green-400 font-semibold">WhatsApp</span>
                </motion.a>

                {/* Email */}
                <motion.a
                  href={`mailto:?subject=Join my SAT Game!&body=${encodeURIComponent(shareMessage)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex flex-col items-center p-3 sm:p-4 bg-white dark:bg-gray-700/50 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-xl transition-all duration-200 border border-gray-200 dark:border-gray-600 hover:border-blue-300 dark:hover:border-blue-700 shadow-md hover:shadow-lg"
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <img src={emailIcon} alt="Email" className="w-8 h-8 sm:w-10 sm:h-10 mb-1 sm:mb-2" />
                  <span className="text-xs sm:text-sm text-blue-700 dark:text-blue-400 font-semibold">Email</span>
                </motion.a>

                {/* Facebook */}
                <motion.a
                  href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(roomLink)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex flex-col items-center p-3 sm:p-4 bg-white dark:bg-gray-700/50 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-xl transition-all duration-200 border border-gray-200 dark:border-gray-600 hover:border-blue-300 dark:hover:border-blue-700 shadow-md hover:shadow-lg"
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <img src={messengerIcon} alt="Facebook" className="w-8 h-8 sm:w-10 sm:h-10 mb-1 sm:mb-2" />
                  <span className="text-xs sm:text-sm text-blue-700 dark:text-blue-400 font-semibold">Facebook</span>
                </motion.a>

                {/* LinkedIn */}
                <motion.a
                  href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(roomLink)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex flex-col items-center p-3 sm:p-4 bg-white dark:bg-gray-700/50 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-xl transition-all duration-200 border border-gray-200 dark:border-gray-600 hover:border-blue-300 dark:hover:border-blue-700 shadow-md hover:shadow-lg"
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <img src={linkedinIcon} alt="LinkedIn" className="w-8 h-8 sm:w-10 sm:h-10 mb-1 sm:mb-2" />
                  <span className="text-xs sm:text-sm text-blue-700 dark:text-blue-400 font-semibold">LinkedIn</span>
                </motion.a>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* No Questions Available Modal */}
      <AnimatePresence>
        {showNoQuestionsModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center"
            onClick={() => setShowNoQuestionsModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="backdrop-blur-xl bg-white/90 dark:bg-gray-800/90 rounded-3xl shadow-2xl p-8 w-full max-w-md mx-4 text-center border border-white/20 dark:border-gray-700/50"
            >
              <div className="mb-6">
                <div className="w-16 h-16 bg-yellow-100 dark:bg-yellow-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-3xl">📚</span>
                </div>
                <h2
                  key={`no-questions-${isDarkMode}`}
                  className="text-2xl sm:text-3xl md:text-4xl font-bold font-zonaProUltra mb-2 relative inline-block"
                  style={{
                    color: isDarkMode ? '#ff7a00' : '#212D82',
                    background: sectionHeadingGradient,
                    backgroundSize: '200% 100%',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                    animation: 'shine 10s linear infinite',
                    lineHeight: '1.2',
                    paddingBottom: '0.25rem'
                  }}
                >
                  No Questions Available
                </h2>
                <p className="text-base sm:text-lg md:text-xl text-gray-600 dark:text-gray-400 mb-4 font-zonaProRegular">
                  {noQuestionsError}
                </p>
                <p className="text-sm sm:text-base text-gray-500 dark:text-gray-400 font-zonaProRegular">
                  Please go back to the lobby and create a new room with a different subject.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <motion.button
                  type="button"
                  onClick={() => {
                    setShowNoQuestionsModal(false);
                    handleLeave();
                  }}
                  className="px-6 py-3 bg-cyan-500 dark:bg-cyan-600 hover:bg-cyan-600 dark:hover:bg-cyan-700 text-white font-zonaProUltra rounded-full transition-colors duration-200"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Go to Lobby
                </motion.button>
                <motion.button
                  type="button"
                  onClick={() => setShowNoQuestionsModal(false)}
                  className="px-6 py-3 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 font-zonaProUltra rounded-full transition-colors duration-200"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Stay in Room
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

    </div>
  );
};

export default MultiplayerWaitingRoom;
